import React, { useState, useEffect, useRef } from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';
import apiService from '../../../services/api';
import { checkDomainAge } from '../../../services/pdfSubmissionService';
import { initiateImageSubmissionScan, getImageSubmissionScanStatus } from '../../../services/discoveryService';


interface ImageSubmissionPageProps {
  onBack: () => void;
  darkMode: boolean;
}

interface Asset {
  id: string;
  source: string;
  original_s3_url: string | null;
  compressed_s3_url: string | null;
  file_format: string;
  file_size_kb: number;
  alt_tag: string;
  title: string;
  description: string;
  file_name: string;
  published_platforms?: string[];
}

interface PlatformResult {
  asset_id: string;
  platform: string;
  da_score: number;
  category: string;
  submission_status: string;
  platform_url: string | null;
}

interface JobDetails {
  job_id: string;
  status: string;
  mode: string;
  assets: Asset[];
  platform_results: PlatformResult[];
  checklist_complete: boolean;
  link_report_url: string | null;
}

const PLATFORM_ICONS: Record<string, string> = {
  pinterest: 'ri-pinterest-line',
  imgur:      'ri-image-2-line',
  flickr:     'ri-flickr-line',
  deviantart: 'ri-artboard-line',
  behance:    'ri-behance-line',
  '4shared':  'ri-folder-shared-line',
};

const PLATFORM_COLORS: Record<string, string> = {
  pinterest: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-100 dark:border-red-800',
  imgur:      'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-100 dark:border-green-800',
  flickr:     'bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 border-pink-100 dark:border-pink-800',
  deviantart: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800',
  behance:    'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800',
  '4shared':  'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-800',
};

export default function ImageSubmissionPage({ onBack, darkMode }: ImageSubmissionPageProps) {
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: 'Implementation / Image Submission',
    routePath: '/dashboard#image-submission',
  });

  const userId = localStorage.getItem('user_id') || '';

  // Core workflow states
  const [mode, setMode] = useState<'new_website' | 'existing_website'>('new_website');
  const [jobId, setJobId] = useState<string | null>(null);
  const [isNewWebsite, setIsNewWebsite] = useState<boolean | null>(null);
  const [domainAgeLoading, setDomainAgeLoading] = useState<boolean>(true);
  const [discoveredAssets, setDiscoveredAssets] = useState<any[]>([]);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditScanId, setAuditScanId] = useState<string | null>(null);
  const [auditCompleted, setAuditCompleted] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill and custom inputs states
  const [brandName, setBrandName] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState<string>('');
  const [brandColors, setBrandColors] = useState<string[]>([]);
  const [logoS3Url, setLogoS3Url] = useState<string>('');
  const [dbKeywords, setDbKeywords] = useState<string[]>([]);
  const [keywordSearch, setKeywordSearch] = useState<string>('');
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [captions, setCaptions] = useState<string[]>(['']);

  // Image Source state
  const [imageSource, setImageSource] = useState<'ai' | 'upload'>('ai');

  // Image Upload / Generation states
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [styleHints, setStyleHints] = useState<string[]>(['professional', 'minimalist']);

  // Platforms and Assets results
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [jobDetails, setJobDetails] = useState<JobDetails | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);

  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);

  // Synchronize selectedAsset with refreshed jobDetails assets
  useEffect(() => {
    if (selectedAsset && jobDetails?.assets) {
      const updated = jobDetails.assets.find(a => a.id === selectedAsset.id);
      if (updated) {
        setSelectedAsset(updated);
      }
    }
  }, [jobDetails]);

  // Sync checkedPlatforms state with selectedAsset's published_platforms
  useEffect(() => {
    if (selectedAsset) {
      const initialChecked: Record<string, boolean> = {};
      const published = selectedAsset.published_platforms || [];
      platforms.forEach(p => {
        initialChecked[p.name] = published.includes(p.name);
      });
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
  }, [selectedAsset, platforms]);

  // URL tracking states
  const [platformUrls, setPlatformUrls] = useState<Record<string, string>>({});
  const [savingPlatform, setSavingPlatform] = useState<Record<string, boolean>>({});
  const [platformErrors, setPlatformErrors] = useState<Record<string, string | null>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Builds a proxy URL that carries the JWT so browsers can load private S3 images
  const getProxyImageUrl = (assetId: string): string => {
    const token = localStorage.getItem('access_token') || '';
    const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
    return `${base}/off-page/image-submission/assets/${assetId}/image?token=${encodeURIComponent(token)}`;
  };

  // Library state
  const [showLibrary, setShowLibrary] = useState<boolean>(false);
  const [libraryAssets, setLibraryAssets] = useState<any[]>([]);
  const [libraryLoading, setLibraryLoading] = useState<boolean>(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [selectedLibraryAsset, setSelectedLibraryAsset] = useState<any | null>(null);

  // Initialize or fetch latest job on mount
  useEffect(() => {
    if (!userId) return;
    initializeJob();
    fetchPlatforms();
    fetchLibrary();
  }, [userId]);

  // Poll for discovery audit results
  useEffect(() => {
    if (!auditScanId || !isAuditing) return;
    const interval = setInterval(async () => {
      try {
        const statusData = await getImageSubmissionScanStatus(auditScanId);
        if (statusData.status === 'COMPLETED') {
          if (statusData.assets) {
            setDiscoveredAssets(statusData.assets.filter((a: any) => a.status === 'ACTIVE'));
          }
          setIsAuditing(false);
          setAuditCompleted(true);
        } else if (statusData.status === 'FAILED') {
          setIsAuditing(false);
          setAuditCompleted(true);
          console.error('Audit failed:', statusData.error_detail);
        }
      } catch (err) {
        console.error('Error polling audit status:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [auditScanId, isAuditing]);
  const handleDiscoveryAudit = async () => {
    setIsAuditing(true);
    setAuditCompleted(false);
    setDiscoveredAssets([]);
    try {
      if (!userId) {
        console.error('No user ID found for audit');
        setIsAuditing(false);
        return;
      }
      const res = await initiateImageSubmissionScan(userId);
      setAuditScanId(res.scan_id);
    } catch (err) {
      console.error('Error initiating audit:', err);
      setIsAuditing(false);
    }
  };

  const getPlatformGaps = () => {
    if (!platforms || platforms.length === 0) return [];
    
    const activeDomains = new Set(
      discoveredAssets
        .filter(asset => asset.status === 'ACTIVE' || asset.metadata_json?.audit?.is_live)
        .map(asset => {
          const urlStr = asset.asset_url || '';
          try {
            const host = new URL(urlStr).hostname;
            return host.toLowerCase().replace('www.', '');
          } catch {
            return (asset.platform_domain || '').toLowerCase().replace('www.', '');
          }
        })
    );

    const activePlatformNames = new Set(
      discoveredAssets
        .filter(asset => asset.status === 'ACTIVE' || asset.metadata_json?.audit?.is_live)
        .map(asset => (asset.platform_name || '').toLowerCase())
    );

    return platforms.filter((p: any) => {
      const pName = p.name.toLowerCase();
      const pUrl = (p.url || '').toLowerCase().replace('www.', '');
      return !activePlatformNames.has(pName) && !activeDomains.has(pUrl);
    });
  };

  // Poll job status if processing
  useEffect(() => {
    let timer: any;
    if (processing && jobId) {
      timer = setInterval(() => {
        pollJobStatus(jobId);
      }, 3000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [processing, jobId]);

  const fetchPlatforms = async () => {
    try {
      const res = await apiService.getImageSubmissionPlatforms();
      setPlatforms(res || []);
    } catch (err) {
      console.error('Error fetching platforms:', err);
    }
  };

  const fetchLibrary = async () => {
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const res = await apiService.getImageSubmissionLibrary();
      setLibraryAssets(res || []);
    } catch (err: any) {
      setLibraryError(err?.message || 'Could not load image library.');
    } finally {
      setLibraryLoading(false);
    }
  };

  const initializeJob = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch prefilled brand settings first
      const bpRes = await apiService.getFullBusinessProfile(userId);
      const profile = bpRes?.data?.business_profile || bpRes?.business_profile || bpRes?.data || bpRes;
      let detectedMode: 'new_website' | 'existing_website' = 'new_website';
      
      if (profile) {
        setBrandName(profile.business_name || '');
        setWebsiteUrl(profile.website_url || '');
        setLogoS3Url(profile.logo_url || '');
        const colors = [];
        if (profile.primary_color) colors.push(profile.primary_color);
        if (profile.secondary_color) colors.push(profile.secondary_color);
        if (profile.other_colors) colors.push(...profile.other_colors);
        setBrandColors(colors);

        // Check domain age
        if (profile.website_url) {
          try {
            setDomainAgeLoading(true);
            const ageRes = await checkDomainAge(profile.website_url);
            setIsNewWebsite(ageRes.is_new_website);
            detectedMode = ageRes.is_new_website ? 'new_website' : 'existing_website';
            setMode(detectedMode);
          } catch (e) {
            console.error('Error checking domain age:', e);
          } finally {
            setDomainAgeLoading(false);
          }
        } else {
          setDomainAgeLoading(false);
        }
      }

      // 2. Create the job using the detected mode
      const res = await apiService.createImageSubmissionJob(userId, detectedMode);
      if (res && res.job_id) {
        setJobId(res.job_id);
        setJobDetails(res);

        if (res.status === 'processing') {
          setProcessing(true);
        } else if (res.status === 'awaiting_platforms' || (res.status === 'completed' && res.assets && res.assets.length > 0)) {
          if (res.assets && res.assets.length > 0) {
            setSelectedAsset(res.assets[res.assets.length - 1]);
          }
        }
      }

      // Fetch locked keywords
      const kwRes = await apiService.getKeywordUniverse(userId);
      const keywordsData = kwRes?.data?.keywords || kwRes?.keywords || [];
      let extractedKeywords: string[] = [];
      let allKws: any[] = [];
      if (Array.isArray(keywordsData)) {
        allKws = keywordsData;
      } else if (typeof keywordsData === 'object') {
        allKws = [...(keywordsData.seo || []), ...(keywordsData.gmb || [])];
      }

      // Filter: active or selected keywords (locked universe set)
      let filteredKws = allKws.filter((k: any) => k.is_active || k.is_selected);
      // Fallback to included ones if no active/selected ones exist yet
      if (filteredKws.length === 0) {
        filteredKws = allKws.filter((k: any) => k.is_included !== false);
      }

      extractedKeywords = filteredKws.map((k: any) => k.keyword);

      setDbKeywords(extractedKeywords);
      if (extractedKeywords.length > 0 && selectedKeywords.length === 0) {
        setSelectedKeywords([extractedKeywords[0]]);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to initialize submission workspace.');
    } finally {
      setLoading(false);
    }
  };

  const pollJobStatus = async (id: string) => {
    try {
      const res = await apiService.getImageSubmissionAssets(id);
      if (res && res.job_id) {
        setJobDetails(res);
        if (['awaiting_platforms', 'completed', 'failed'].includes(res.status)) {
          setProcessing(false);
          if (res.status === 'awaiting_platforms' || res.status === 'completed') {
            if (res.assets && res.assets.length > 0) {
              setSelectedAsset(res.assets[res.assets.length - 1]);
            }
            fetchLibrary();
          } else {
            setError('Background processing of image assets failed.');
          }
        }
      }
    } catch (err: any) {
      console.error('Error polling job status:', err);
      setProcessing(false);
      setError(err?.message || 'Session expired or network error occurred during polling.');
    }
  };

  const handleGenerateOrOptimize = async () => {
    if (!jobId) return;
    if (selectedKeywords.length === 0) {
      setError('Please select a target ALT keyword.');
      return;
    }
    setError(null);
    setLoading(true);
    setProcessing(true);
    setSelectedAsset(null);
    try {
      // 1. Save inputs
      const inputs = {
        job_id: jobId,
        brand_name: brandName,
        website_url: websiteUrl,
        target_alt_keywords: selectedKeywords,
        brand_image_urls: jobDetails?.assets.filter(a => a.source === 'uploaded').map(a => a.original_s3_url) || [],
        product_photo_urls: [],
        image_captions: captions.filter(c => c.trim() !== '')
      };
      await apiService.saveImageSubmissionInputs(jobId, inputs);

      // 2. Either trigger upload processing or AI generation
      if (imageSource === 'upload') {
        if (uploadedFiles.length === 0) {
          setError('Please upload at least one image file.');
          setProcessing(false);
          setLoading(false);
          return;
        }
        await apiService.uploadImageSubmissionFiles(jobId, uploadedFiles);
        setUploadedFiles([]);
      } else {
        const payload = {
          job_id: jobId,
          brand_name: brandName,
          brand_colors: brandColors,
          logo_s3_url: logoS3Url || null,
          style_hints: styleHints,
          count: 1
        };
        await apiService.triggerAIImageGeneration(jobId, payload);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to trigger optimization/generation.');
      setProcessing(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadedFiles(prev => [...prev, ...Array.from(files)]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploadedFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  const removeUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleTogglePlatformCheckbox = (platformName: string) => {
    setCheckedPlatforms(prev => ({
      ...prev,
      [platformName]: !prev[platformName]
    }));
  };

  const handleMarkComplete = async () => {
    if (!selectedAsset) return;
    setIsMarkingComplete(true);
    setError(null);
    try {
      const selectedPlatforms = Object.keys(checkedPlatforms).filter(p => checkedPlatforms[p]);
      await apiService.completeImageSubmission(selectedAsset.id, { platforms: selectedPlatforms });
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
      
      // Refresh assets to get updated published_platforms
      if (jobId) {
        const res = await apiService.getImageSubmissionAssets(jobId);
        setJobDetails(res);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to update complete status. Please try again.');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const addCaptionField = () => {
    setCaptions(prev => [...prev, '']);
  };

  const removeCaptionField = (idx: number) => {
    setCaptions(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCaptionValue = (idx: number, val: string) => {
    setCaptions(prev => {
      const clone = [...prev];
      clone[idx] = val;
      return clone;
    });
  };

  const toggleKeyword = (kw: string) => {
    setSelectedKeywords(prev => prev.includes(kw) ? [] : [kw]);
  };

  const filteredKeywords = dbKeywords.filter(k =>
    k.toLowerCase().includes(keywordSearch.toLowerCase())
  );

  return (
    <div className={`space-y-6 pb-24 ${darkMode ? 'dark text-white' : 'text-gray-900'}`}>
      {/* Top Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer border-none bg-transparent outline-none"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back to Off-Page Content
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <i className="ri-image-line text-blue-500"></i> Image Submission Optimizer
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Optimize files to WebP or generate branded AI graphics. Add alt tags automatically, submit, and track live URLs.
            </p>
          </div>

          {/* Website State Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 border border-gray-200 dark:border-gray-700">
            {domainAgeLoading ? (
              <span className="px-4 py-2 text-xs text-gray-400 flex items-center gap-1.5"><i className="ri-loader-4-line animate-spin" /> Detecting…</span>
            ) : (
              <>
                <button
                  onClick={() => setMode('new_website')}
                  disabled={isNewWebsite === false}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center border-none ${
                    isNewWebsite === false ? 'opacity-50 cursor-not-allowed bg-transparent' : 'cursor-pointer'
                  } ${
                    mode === 'new_website'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-transparent'
                  }`}
                >
                  New Website
                  {isNewWebsite === false && <i className="ri-lock-line ml-1.5"></i>}
                </button>
                <button
                  onClick={() => setMode('existing_website')}
                  disabled={isNewWebsite === true}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center border-none ${
                    isNewWebsite === true ? 'opacity-50 cursor-not-allowed bg-transparent' : 'cursor-pointer'
                  } ${
                    mode === 'existing_website'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-transparent'
                  }`}
                >
                  Existing Website
                  {isNewWebsite === true && <i className="ri-lock-line ml-1.5"></i>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showLibrary ? (
        /* ── Image Library View ─────────────────────────────────────────── */
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-2xl p-6 text-white border border-blue-500/20 shadow-md relative overflow-hidden">
            <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 text-[150px] text-blue-500/10 pointer-events-none">
              <i className="ri-archive-stack-line" />
            </div>
            <div className="flex items-center justify-between gap-4 relative z-10">
              <div>
                <h3 className="text-xl font-bold mb-1">Image Library</h3>
                <p className="text-xs text-blue-200">History of all generated or uploaded optimized images (30-day retention).</p>
              </div>
              <button onClick={fetchLibrary} disabled={libraryLoading} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-xs font-bold transition-colors cursor-pointer">
                <i className={`ri-refresh-line ${libraryLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          {libraryLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
              <i className="ri-loader-4-line text-4xl animate-spin text-blue-500 mb-4" />
              <p className="text-sm font-medium">Loading image gallery...</p>
            </div>
          ) : libraryAssets.length === 0 ? (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
              <i className="ri-image-add-line text-4xl mb-3 block" />
              <p>No images in your library yet.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm font-bold text-gray-800 dark:text-white">{libraryAssets.length} image{libraryAssets.length !== 1 ? 's' : ''} saved</span>
              </div>
              <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {libraryAssets.map(asset => (
                  <div key={asset.id} onClick={() => setSelectedLibraryAsset(asset)}
                    className="group relative cursor-pointer rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-md transition-all bg-gray-50 dark:bg-gray-850">
                    <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
                      <img src={getProxyImageUrl(asset.id)} alt={asset.alt_tag || 'gallery'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[8px] font-bold uppercase ${asset.source === 'ai_generated' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}`}>
                        {asset.source === 'ai_generated' ? 'AI' : 'Upload'}
                      </span>
                    </div>
                    <div className="p-2">
                      <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 truncate" title={asset.file_name}>{asset.file_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedLibraryAsset && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelectedLibraryAsset(null)}>
              <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>
                <button onClick={() => setSelectedLibraryAsset(null)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 border-none cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
                <div className="bg-gray-950 flex items-center justify-center p-4 relative" style={{ minHeight: '260px' }}>
                  <img src={getProxyImageUrl(selectedLibraryAsset.id)} alt={selectedLibraryAsset.alt_tag} className="max-h-72 w-full object-contain" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${selectedLibraryAsset.source === 'ai_generated' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}`}>
                      {selectedLibraryAsset.source === 'ai_generated' ? '✦ AI Generated' : '↑ Uploaded'}
                    </span>
                    <span className="px-2 py-0.5 bg-black/50 rounded-full text-[10px] font-bold text-white">
                      {selectedLibraryAsset.file_format} · {selectedLibraryAsset.file_size_kb} KB
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-8 space-y-4">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white truncate">{selectedLibraryAsset.file_name}</h3>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                      <span className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold block mb-1">ALT Tag</span>
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{selectedLibraryAsset.alt_tag || '—'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                      <span className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold block mb-1">Title</span>
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{selectedLibraryAsset.title || '—'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                      <span className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold block mb-1">Description</span>
                      <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{selectedLibraryAsset.description || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                    <span className="text-xs text-gray-400">Created {selectedLibraryAsset.created_at ? new Date(selectedLibraryAsset.created_at).toLocaleDateString() : '—'}</span>
                    <a href={getProxyImageUrl(selectedLibraryAsset.id)} download={selectedLibraryAsset.file_name || 'image.webp'} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition-colors border-none cursor-pointer">
                      <i className="ri-download-2-line" /> Download Image
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── Main Optimizer View ─────────────────────────────────────── */
        <div className="space-y-6">
          {loading && !processing ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
              <i className="ri-loader-4-line text-4xl animate-spin text-blue-500 mb-4" />
              <p className="text-sm font-medium">Loading Workspace...</p>
            </div>
          ) : (
            <>
              {/* Business Profile details */}
              {brandName && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl p-5 flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-building-2-line text-blue-600 dark:text-blue-400 text-lg" />
                      <span className="font-semibold text-blue-800 dark:text-blue-200">Business Profile (Auto-filled from Onboarding)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                      {[
                        { label: 'Business Name', value: brandName, icon: 'ri-store-2-line' },
                        { label: 'Website', value: websiteUrl, icon: 'ri-global-line' },
                      ].map(item => (
                        <div key={item.label} className="flex items-start gap-2">
                          <i className={`${item.icon} text-blue-500 mt-0.5`} />
                          <div>
                            <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">{item.label}</div>
                            <div className="text-blue-900 dark:text-blue-100">{item.value || '—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {isNewWebsite !== null && (
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${isNewWebsite ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                      {isNewWebsite ? 'New Domain' : 'Established Domain'}
                    </div>
                  )}
                </div>
              )}

              {/* Keyword Selection */}
              {dbKeywords.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                    <i className="ri-key-2-line text-blue-500" /> Select Target ALT Keyword
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Choose the primary keyword to anchor your optimized alt metadata tag.</p>
                  <input type="text" value={keywordSearch} onChange={e => setKeywordSearch(e.target.value)} placeholder="Search keywords..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-3" />
                  <div className="flex flex-wrap gap-2">
                    {filteredKeywords.slice(0, 20).map(kw => (
                      <button key={kw} onClick={() => toggleKeyword(kw)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${selectedKeywords.includes(kw) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400'}`}>
                        {kw}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ─── Audit Existing Image Submissions ─── */}
              {mode === 'existing_website' && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                        Audit Existing Image Submissions
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Use our crawler engine to discover and audit existing images published for your brand across the web.
                      </p>
                    </div>
                    <button
                      onClick={handleDiscoveryAudit}
                      disabled={isAuditing}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 border-none cursor-pointer ${
                        isAuditing 
                          ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      }`}
                    >
                      {isAuditing ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Auditing...</>
                      ) : (
                        <><i className="ri-search-eye-line"></i> Run Discovery Audit</>
                      )}
                    </button>
                  </div>

                  {isAuditing && (
                    <div className="py-8 flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Crawling the web for existing image submissions...</p>
                    </div>
                  )}

                  {auditCompleted && discoveredAssets.length === 0 && (
                    <div className="py-6 text-center border-t border-gray-100 dark:border-gray-700 mt-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-55 dark:bg-gray-800 mb-3">
                        <i className="ri-search-line text-xl text-gray-400"></i>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">No existing image submissions found</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                        No existing image submissions found. Our crawler couldn't find any images published for your website.
                      </p>
                    </div>
                  )}

                  {auditCompleted && discoveredAssets.length > 0 && (
                    <div className="mt-4 space-y-6">
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                          <thead className="bg-gray-50 dark:bg-gray-800/50">
                            <tr>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Platform</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Asset Title &amp; URL</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Live Status</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Link Equity</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Profile Completeness</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Overall Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {discoveredAssets.map((asset) => {
                              const audit = asset.metadata_json?.audit;
                              const isLive = audit ? audit.is_live : (asset.status === 'ACTIVE');
                              const backlinkFound = audit?.backlink_found;
                              const backlinkType = audit?.backlink_type;
                              const completenessScore = audit?.completeness_score ?? 0;
                              const completenessStatus = audit?.completeness_status ?? 'Minimal';
                              const overallStatus = audit?.status || (isLive ? 'Optimal' : 'Critical');
                              const notes = audit?.notes || '';
                              const pNameLower = (asset.platform_name || '').toLowerCase();

                              return (
                                <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-sm">
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-6.5 h-6.5 rounded-lg border flex items-center justify-center flex-shrink-0 ${
                                        PLATFORM_COLORS[pNameLower] || 'bg-gray-50 dark:bg-gray-800 border-gray-200 text-gray-500'
                                      }`}>
                                        <i className={`${PLATFORM_ICONS[pNameLower] || 'ri-global-line'} text-sm`} />
                                      </div>
                                      <span className="font-semibold text-gray-900 dark:text-white capitalize">
                                        {asset.platform_name || asset.platform_domain || 'Unknown'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 max-w-[280px]">
                                    <div className="font-medium text-gray-900 dark:text-white truncate" title={asset.asset_title || ''}>
                                      {asset.asset_title || 'Untitled Discovery'}
                                    </div>
                                    {isLive ? (
                                      <a
                                        href={asset.asset_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 dark:text-blue-400 font-mono hover:underline flex items-center gap-0.5 truncate mt-0.5"
                                      >
                                        {asset.asset_url} <i className="ri-external-link-line text-[9px]"></i>
                                      </a>
                                    ) : (
                                      <span
                                        className="text-xs text-gray-400 dark:text-gray-500 font-mono flex items-center gap-0.5 truncate mt-0.5 cursor-not-allowed"
                                        title="This link is dead and cannot be opened."
                                      >
                                        {asset.asset_url}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {isLive ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50">
                                        Live
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50" title={notes}>
                                        Dead
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {audit ? (
                                      backlinkFound ? (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
                                          backlinkType === 'dofollow'
                                            ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/50'
                                            : 'bg-amber-50 dark:bg-amber-955/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900/50'
                                        }`}>
                                          {backlinkType}
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50">
                                          None
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {audit ? (
                                      <div className="flex items-center gap-2" title={`Score: ${completenessScore}%, Status: ${completenessStatus}`}>
                                        <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                          <div 
                                            className={`h-full rounded-full ${
                                              completenessScore >= 80 ? 'bg-emerald-500' : completenessScore >= 40 ? 'bg-amber-500' : 'bg-red-500'
                                            }`} 
                                            style={{ width: `${completenessScore}%` }}
                                          />
                                        </div>
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{completenessScore}%</span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${
                                      overallStatus === 'Optimal'
                                        ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800 text-emerald-500'
                                        : overallStatus === 'Critical'
                                        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 text-red-500'
                                        : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-amber-500'
                                    }`}>
                                      {overallStatus}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Platform Gaps Panel */}
                      {(() => {
                        const gaps = getPlatformGaps();
                        if (gaps.length === 0) return null;
                        return (
                          <div className="p-5 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-950/5">
                            <div className="flex items-start gap-3">
                              <i className="ri-error-warning-line text-amber-600 dark:text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white">Platform Checklist Gaps Detected</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Your backlink profile is currently missing active image submissions on these high-authority platforms:
                                </p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {gaps.map((p: any) => {
                                    const pNameLower = p.name.toLowerCase();
                                    return (
                                      <span key={p.name} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                                        <div className={`w-4.5 h-4.5 rounded border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[pNameLower] || 'bg-gray-100 text-gray-500'}`}>
                                          <i className={`${PLATFORM_ICONS[pNameLower] || 'ri-global-line'} text-[10px]`} />
                                        </div>
                                        {p.name} (DA {p.da})
                                      </span>
                                    );
                                  })}
                                </div>
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 italic">
                                  Tip: Switch to the "New Website" view above to generate and submit optimized image packages to these platforms to fill this gap.
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}


                  {/* Image Source Selection */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <i className="ri-image-line text-blue-500" /> AI Graphics Generation
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Configure parameters to generate high-quality branded graphics using AI.</p>

                    <div className="flex flex-col gap-1.5 pt-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Style Theme</label>
                      <select
                        value={styleHints[0] || 'professional, minimalist'}
                        onChange={(e) => setStyleHints([e.target.value])}
                        className="bg-gray-55 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm px-3.5 py-2.5 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 w-full"
                      >
                        <option value="professional, minimalist">Professional & Minimalist</option>
                        <option value="neon, futuristic">Neon/Cyberpunk</option>
                        <option value="watercolor, elegant">Watercolor & Elegant</option>
                        <option value="corporate vector graphic">Corporate Vector Graphic</option>
                      </select>
                    </div>
                  </div>

                  {/* Suggested Platforms Preview */}
                  {platforms.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <i className="ri-global-line text-blue-500" /> Suggested Platforms
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {platforms.map(p => (
                          <div key={p.name} className="bg-gray-55 dark:bg-gray-700/50 rounded-xl p-3 text-center border border-gray-100 dark:border-gray-700">
                            <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mx-auto mb-2 ${PLATFORM_COLORS[p.name.toLowerCase()] || 'bg-gray-100'}`}>
                              <i className={`${PLATFORM_ICONS[p.name.toLowerCase()] || 'ri-global-line'} text-xl`} />
                            </div>
                            <div className="text-xs font-semibold text-gray-900 dark:text-white capitalize">{p.name}</div>
                            <div className="text-xs text-gray-400 mt-0.5">DA {p.da}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <i className="ri-error-warning-line text-red-500 mt-0.5" />
                      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    </div>
                  )}

                  {/* Main Generate Button */}
                  <button onClick={handleGenerateOrOptimize} disabled={processing || selectedKeywords.length === 0}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-base flex items-center justify-center gap-3 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200 dark:shadow-blue-900/30 border-none cursor-pointer">
                    <i className="ri-sparkling-2-line text-xl" />
                    {processing ? 'Processing Assets…' : jobDetails?.assets && jobDetails.assets.length > 0 ? 'Re-optimize & Generate Assets' : 'Optimize & Generate Assets'}
                  </button>

                  {/* Loading / Processing spinner */}
                  {processing && (
                    <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                      <i className="ri-loader-4-line text-4xl animate-spin text-blue-500 mb-4" />
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Processing, compressing, and generating SEO tags for your image assets…</p>
                    </div>
                  )}

                  {/* Consolidated Results Section */}
                  {jobDetails && jobDetails.assets && jobDetails.assets.length > 0 && !processing && (
                    <div className="space-y-6">
                      {/* Export ZIP package banner */}
                      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-md flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-2">
                          <h3 className="text-xl font-bold">Manual Submission Package Ready!</h3>
                          <p className="text-xs text-blue-100">
                            We've bundled all optimized WebP images along with an <span className="font-semibold">SEO_Metadata_Guide.txt</span> guide containing titles, descriptions, and ALT keywords.
                          </p>
                        </div>
                        <a
                          href={`${apiService.baseUrl}/off-page/image-submission/jobs/${jobDetails.job_id}/manual-submission-package?token=${encodeURIComponent(localStorage.getItem('access_token') || '')}${selectedAsset ? `&selected_asset_id=${selectedAsset.id}` : ''}`}
                          download
                          className="px-6 py-3 bg-white text-blue-700 hover:bg-blue-50 font-bold rounded-xl text-sm transition-all flex items-center gap-2 shadow-sm text-center border-none decoration-none"
                        >
                          <i className="ri-download-2-line text-lg animate-bounce"></i> Download Selected ZIP
                        </a>
                      </div>

                      {/* Optimized Assets Grid */}
                      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <h3 className="text-lg font-bold mb-1">Optimized Assets</h3>
                        <p className="text-xs text-gray-400 mb-4">Click an asset to view details and track submissions below.</p>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {jobDetails.assets.map((asset) => (
                            <div
                              key={asset.id}
                              onClick={() => setSelectedAsset(asset)}
                              className={`group relative cursor-pointer rounded-xl overflow-hidden border transition-all bg-gray-55 dark:bg-gray-900/50 hover:shadow-md ${
                                selectedAsset?.id === asset.id
                                  ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'
                              }`}
                            >
                              <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
                                {asset.id ? (
                                  <img
                                    src={getProxyImageUrl(asset.id)}
                                    alt={asset.alt_tag}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <i className="ri-image-line text-2xl text-gray-300"></i>
                                  </div>
                                )}
                                <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-black/70 text-white rounded text-[8px] font-bold uppercase">
                                  {asset.file_format} | {asset.file_size_kb} KB
                                </span>
                              </div>
                              <div className="p-2">
                                <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 truncate" title={asset.file_name}>
                                  {asset.file_name}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Selected Asset Details + Platform Tracker */}
                      {selectedAsset && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Asset Info Card */}
                          <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="font-bold text-sm text-gray-900 dark:text-white truncate">Active Asset Info</h4>
                              <button
                                onClick={() => setPreviewAsset(selectedAsset)}
                                className="px-2.5 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-bold rounded-lg border-none cursor-pointer flex items-center gap-1"
                              >
                                <i className="ri-zoom-in-line" /> Preview
                              </button>
                            </div>
                            
                            <div className="aspect-video bg-gray-100 dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-850 flex items-center justify-center">
                              <img
                                src={getProxyImageUrl(selectedAsset.id)}
                                alt={selectedAsset.alt_tag}
                                className="max-h-full max-w-full object-contain"
                              />
                            </div>

                            <div className="space-y-3">
                              <div className="p-3 bg-gray-55 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                                <div className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Alt Tag</div>
                                <div className="text-xs text-gray-700 dark:text-gray-300 font-medium break-words leading-relaxed">{selectedAsset.alt_tag}</div>
                              </div>
                              <div className="p-3 bg-gray-55 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                                <div className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Title</div>
                                <div className="text-xs text-gray-700 dark:text-gray-300 font-medium break-words leading-relaxed">{selectedAsset.title}</div>
                              </div>
                              <div className="p-3 bg-gray-55 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                                <div className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Description</div>
                                <div className="text-xs text-gray-700 dark:text-gray-300 font-medium break-words leading-relaxed">{selectedAsset.description}</div>
                              </div>
                            </div>
                          </div>

                          {/* Platforms Tracker for selected asset */}
                          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                  <i className="ri-global-line text-blue-500" /> Suggested Platforms — Submission Checklist
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Submit this asset manually to the suggested image platforms. Check them off once done.
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {markCompleteSuccess && (
                                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                    <i className="ri-checkbox-circle-fill text-base" /> Saved!
                                  </span>
                                )}
                                <button
                                  onClick={handleMarkComplete}
                                  disabled={isMarkingComplete || !Object.values(checkedPlatforms).some(Boolean)}
                                  className={`px-4 py-2 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-sm cursor-pointer border-none ${
                                    Object.values(checkedPlatforms).some(Boolean)
                                      ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow'
                                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                                  }`}
                                >
                                  {isMarkingComplete ? (
                                    <>
                                      <i className="ri-loader-4-line animate-spin" /> Saving...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-checkbox-circle-line" /> Mark as Complete
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              {platforms.map(p => {
                                const isChecked = !!checkedPlatforms[p.name];

                                return (
                                  <div 
                                    key={p.name} 
                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                                      isChecked 
                                        ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/50' 
                                        : 'bg-gray-55 dark:bg-gray-900/30 border-gray-100 dark:border-gray-800'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleTogglePlatformCheckbox(p.name)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                      />
                                      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[p.name.toLowerCase()] || 'bg-gray-100'}`}>
                                        <i className={`${PLATFORM_ICONS[p.name.toLowerCase()] || 'ri-global-line'} text-base`} />
                                      </div>
                                      <div>
                                        <span className={`text-sm font-semibold text-gray-900 dark:text-white capitalize ${isChecked ? 'line-through text-gray-400 dark:text-gray-500 font-medium' : ''}`}>{p.name}</span>
                                        <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">DA {p.da}</span>
                                      </div>
                                    </div>
                                    
                                    <a
                                      href={`https://${p.name.toLowerCase()}.com`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold decoration-none"
                                    >
                                      Visit Site <i className="ri-external-link-line" />
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
            </>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={() => setPreviewAsset(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full relative border border-gray-100 dark:border-gray-800" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <h3 className="font-bold text-sm text-gray-900 dark:text-white truncate max-w-[80%]">{previewAsset.file_name}</h3>
              <button onClick={() => setPreviewAsset(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white border-none cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="bg-gray-950 flex items-center justify-center p-6" style={{ minHeight: '350px' }}>
              <img src={getProxyImageUrl(previewAsset.id)} alt={previewAsset.alt_tag} className="max-h-[60vh] max-w-full object-contain rounded-lg" />
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800 space-y-2">
              <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-bold text-gray-700 dark:text-gray-300">Alt Tag:</span> {previewAsset.alt_tag}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-bold text-gray-700 dark:text-gray-300">Title:</span> {previewAsset.title}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
